import SearchInput from "@components/common/SearchInput";
import { Modal } from "flowbite-react";
import { useEffect, useMemo, useState } from "react";
import { customModalTheme } from "styles/customTheme";

const DEFAULT_INCREMENT = 20;

export const ModalOptimaNumberVouchers = ({
	isOpen,
	closeModal,
	day,
	online_vouchers = [],
	increment = DEFAULT_INCREMENT,
	initialCount = increment,
}) => {
	const [searchTerms, setSearchTerms] = useState({ keyword: "" });
	const [visibleCount, setVisibleCount] = useState(initialCount);

	useEffect(() => {
		if (isOpen) {
			setSearchTerms({ keyword: "" });
			setVisibleCount(initialCount);
		}
	}, [isOpen, day, initialCount]);

	const handleSearchChange = (id, value) => {
		setSearchTerms((prev) => ({ ...prev, [id]: value }));
		setVisibleCount(initialCount);
	};

	const handleClearSearch = (id) => {
		setSearchTerms((prev) => ({ ...prev, [id]: "" }));
		setVisibleCount(initialCount);
	};

	const filteredVouchers = useMemo(() => {
		const keyword = searchTerms.keyword?.trim().toLowerCase();
		if (!keyword) return online_vouchers;

		return online_vouchers.filter(
			(v) =>
				v.optima_number?.toLowerCase().includes(keyword) ||
				v.username?.toLowerCase().includes(keyword),
		);
	}, [online_vouchers, searchTerms.keyword]);

	const totalItems = filteredVouchers.length;
	const visibleVouchers = filteredVouchers.slice(0, visibleCount);
	const hasMore = visibleCount < totalItems;
	const remaining = totalItems - visibleCount;

	const handleSeeMore = () => {
		setVisibleCount((prev) => Math.min(prev + increment, totalItems));
	};

	const handleShowLess = () => {
		setVisibleCount(initialCount);
	};

	const hasOptimaNumber = online_vouchers?.some((item) => item?.optima_number != null);

	return (
		<>
			<Modal
				show={isOpen}
				onClose={closeModal}
				size={hasOptimaNumber ? "2xl" : "xl"}
				theme={customModalTheme}
			>
				<Modal.Header className="border-b-slate-400">
					<div className={hasOptimaNumber ? "w-full" : "mx-auto w-64"}>
						<h2
							className="truncate font-semibold text-sm text-white lg:text-lg"
							data-testid="transfer-modal_title"
						>
							{day}
						</h2>

						{/* <p className="mt-1 truncate text-blue-gray text-sm">{day}</p> */}
					</div>
				</Modal.Header>

				<Modal.Body className="border-border_bottom text-gray-800">
					{/* Title + count on left, search on right */}
					{online_vouchers?.length != 0 && (
						<div className="flex items-end justify-end pb-4">
							<div className="w-full max-w-xs">
								<SearchInput
									id="keyword"
									placeholder={"Search"}
									value={searchTerms.keyword}
									onChange={handleSearchChange}
									onClear={handleClearSearch}
								/>
							</div>
						</div>
					)}

					{visibleVouchers.length === 0 ? (
						<div className="flex flex-col items-center justify-center gap-2 py-12 text-center">
							<p className="text-gray-400 text-xs">
								{/* {online_vouchers?.length === 0 ? "No online vouchers." : "No available data."} */}
								No available data
							</p>
						</div>
					) : (
						<div className="mt-4">
							{/* Column headers */}
							<div
								className={`border-blue-gray border-b px-3 pb-2 ${
									hasOptimaNumber
										? "flex items-center justify-between"
										: "flex items-start justify-start"
								}`}
							>
								<span className="font-semibold text-[11px] text-gray-400 uppercase tracking-wide">
									Username
								</span>
								{hasOptimaNumber && (
									<span className="font-semibold text-[11px] text-gray-400 uppercase tracking-wide">
										Optima Number
									</span>
								)}
							</div>

							{/* List */}
							<ul className="custom-scrollbar max-h-[360px] divide-y divide-blue-gray overflow-y-auto">
								{visibleVouchers.map((v, i) => (
									<li
										key={`${v.username}-${i}`}
										className={`px-3 py-2.5 transition-colors hover:bg-blue-gray hover:bg-opacity-20 ${
											hasOptimaNumber
												? "flex items-center justify-between"
												: "flex items-start justify-start"
										}`}
									>
										<span className="truncate text-white text-xs">{v.username}</span>

										{hasOptimaNumber && (
											<span className="ml-4 shrink-0 rounded-md bg-blue-gray/90 px-2 py-1 font-mono font-semibold text-dark-blue text-xs">
												{v.optima_number}
											</span>
										)}
									</li>
								))}
							</ul>

							{/* See more / Show less */}
							{(hasMore || visibleCount > initialCount) && (
								<div className="mt-2 flex items-center justify-center gap-4 pt-4">
									{hasMore && (
										<button
											type="button"
											onClick={handleSeeMore}
											className="rounded-md border border-gray-600 px-4 py-1.5 font-medium text-gray-200 text-xs transition-colors hover:border-blue-gray/80 hover:bg-blue-gray/80 hover:text-gray-300 hover:text-gray-60"
										>
											See more <span className="text-gray-400">({remaining} left)</span>
										</button>
									)}
									{visibleCount > initialCount && (
										<button
											type="button"
											onClick={handleShowLess}
											className="font-medium text-gray-300 text-xs hover:text-gray-400"
										>
											Show less
										</button>
									)}
								</div>
							)}
						</div>
					)}
				</Modal.Body>
			</Modal>
		</>
	);
};
